import React, {
    createContext,
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    useContext,
    useState,
} from 'react'
import {
    ApolloQueryResult,
    InternalRefetchQueriesInclude,
} from '@apollo/client'

import { useClinic } from '~contexts/clinic-context'
import {
    FilterOp,
    OrderByInput,
    QueryInput,
    InitiativeFieldsFragment,
    QueryInitiativesQuery,
    useQueryInitiativesQuery,
    namedOperations,
} from '~graphql/generated/graphql'

import { useDebounce } from '~utils/hooks/use-debounce'
import { eqStringFilter, eqBoolFilter } from '~utils/query-helpers'
import { useBoolean } from '@chakra-ui/react'

const InitiativeContext = createContext<InitiativesProviderType>(
    {} as InitiativesProviderType,
)

export function InitiativesProvider({ children }: PropsWithChildren) {
    const { clinicGroup } = useClinic()
    const [searchString, setSearchString] = useState('')
    const [showArchived, setShowArchived] = useBoolean()

    const debouncedSearchString = useDebounce(searchString, 300)

    const filters: QueryInput[] = [
        eqStringFilter('clinic_group', clinicGroup?.id),
        ...(!showArchived ? [eqBoolFilter('is_archived', false)] : []),
        {
            column: 'name',
            filterOp: FilterOp.Ilike,
            value: {
                string: `%${debouncedSearchString}%`,
            },
        },
    ]

    const orderBy: OrderByInput = {
        column: 'created_at',
        isAscending: false,
    }

    const { data, loading, error, refetch } = useQueryInitiativesQuery({
        variables: {
            where: filters,
            orderBy,
        },
        skip: !clinicGroup,
    })

    const initiatives = data?.queryInitiatives || []

    return (
        <InitiativeContext.Provider
            value={{
                searchString,
                setSearchString,
                showArchived,
                toggleArchived: setShowArchived.toggle,
                initiatives,
                refetchInitiatives: refetch,
                isLoading: loading,
                refetchQueries: [namedOperations.Query.queryInitiatives],
            }}
        >
            {children}
        </InitiativeContext.Provider>
    )
}

type InitiativesProviderType = {
    searchString: string
    setSearchString: Dispatch<SetStateAction<string>>
    showArchived: boolean
    toggleArchived: () => void
    initiatives: InitiativeFieldsFragment[]
    refetchInitiatives: () => Promise<ApolloQueryResult<QueryInitiativesQuery>>
    refetchQueries: InternalRefetchQueriesInclude
    isLoading: boolean
}

export function useInitiatives(): InitiativesProviderType {
    return useContext(InitiativeContext)
}
